import React from 'react';
import { FilePlus, Paperclip, FileText, CalendarClock, Plus, X } from 'lucide-react';
import { useDiscardGuard } from '../../../../hooks/useDiscardGuard';

/** Mobiler FAB (Mehrfachaktion) + Bottom-Sheet für schnelle Notizen. */
export function LeadDetailMobileActions({
  activeMobileSection,
  fabFileInputRef,
  handleUploadDocument,
  showFabMenu,
  setShowFabMenu,
  setShowCreateDocumentModal,
  setMobileNoteContent,
  setShowMobileNoteSheet,
  setShowCreateActivityModal,
  showMobileNoteSheet,
  mobileNoteContent,
  saveNotes,
  isSaving,
}: {
  activeMobileSection: 'details' | 'zusammenfassung' | 'angebot' | 'dokumente' | 'kosten';
  fabFileInputRef: React.RefObject<HTMLInputElement | null>;
  handleUploadDocument: (e: React.ChangeEvent<HTMLInputElement>) => void;
  showFabMenu: boolean;
  setShowFabMenu: React.Dispatch<React.SetStateAction<boolean>>;
  setShowCreateDocumentModal: React.Dispatch<React.SetStateAction<boolean>>;
  setMobileNoteContent: React.Dispatch<React.SetStateAction<string>>;
  setShowMobileNoteSheet: React.Dispatch<React.SetStateAction<boolean>>;
  setShowCreateActivityModal: React.Dispatch<React.SetStateAction<boolean>>;
  showMobileNoteSheet: boolean;
  mobileNoteContent: string;
  saveNotes: (content?: string) => Promise<void> | void;
  isSaving: boolean;
}) {
  // Daneben tippen oder ✕ mit Text: erst nachfragen — die Notiz wäre sonst beim nächsten Öffnen weg.
  const noteDiscardGuard = useDiscardGuard({
    dirty: showMobileNoteSheet && mobileNoteContent.trim() !== '',
    onClose: () => {
      setMobileNoteContent('');
      setShowMobileNoteSheet(false);
    },
    disabled: isSaving,
    description: 'Die Notiz ist noch nicht gespeichert und geht verloren.',
  });

  return (
    <>
      {/* Mobile FAB — multi-action */}
      {activeMobileSection === 'zusammenfassung' && (
        <div className="fixed bottom-20 right-5 z-30 md:hidden flex flex-col items-end gap-3">
          <input ref={fabFileInputRef} type="file" className="hidden" accept="*/*" multiple onChange={handleUploadDocument} />
          {/* FAB menu items */}
          {showFabMenu && (
            <>
              {/* backdrop */}
              <div className="fixed inset-0 z-[-1]" onClick={() => setShowFabMenu(false)} />
              <div className="flex flex-col items-end gap-2 mb-1">
                {/* Dokument erstellen */}
                <button
                  type="button"
                  onClick={() => { setShowFabMenu(false); setShowCreateDocumentModal(true); }}
                  className="flex items-center gap-2 bg-app-surface rounded-full px-4 py-2.5 shadow-lg border border-app-border text-sm font-medium text-app-text-muted"
                >
                  <FilePlus className="w-4 h-4 text-app-text-subtle" />
                  Dokument erstellen
                </button>
                {/* Datei hochladen */}
                <button
                  type="button"
                  onClick={() => { setShowFabMenu(false); fabFileInputRef.current?.click(); }}
                  className="flex items-center gap-2 bg-app-surface rounded-full px-4 py-2.5 shadow-lg border border-app-border text-sm font-medium text-app-text-muted"
                >
                  <Paperclip className="w-4 h-4 text-app-text-subtle" />
                  Datei hochladen
                </button>
                {/* Notiz */}
                <button
                  type="button"
                  onClick={() => {
                    setShowFabMenu(false);
                    setMobileNoteContent('');
                    setShowMobileNoteSheet(true);
                  }}
                  className="flex items-center gap-2 bg-app-surface rounded-full px-4 py-2.5 shadow-lg border border-app-border text-sm font-medium text-app-text-muted"
                >
                  <FileText className="w-4 h-4 text-app-text-subtle" />
                  Notiz bearbeiten
                </button>
                {/* Aktivität */}
                <button
                  type="button"
                  onClick={() => { setShowFabMenu(false); setShowCreateActivityModal(true); }}
                  className="flex items-center gap-2 bg-app-surface rounded-full px-4 py-2.5 shadow-lg border border-app-border text-sm font-medium text-app-text-muted"
                >
                  <CalendarClock className="w-4 h-4 text-app-text-subtle" />
                  Aktivität planen
                </button>
              </div>
            </>
          )}
          {/* Main FAB button */}
          <button
            type="button"
            onClick={() => setShowFabMenu((v) => !v)}
            className={`h-14 w-14 rounded-full text-white shadow-lg flex items-center justify-center transition-all ${showFabMenu ? 'bg-slate-700 rotate-45' : 'bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800'}`}
            aria-label="Aktionen"
          >
            <Plus className="w-6 h-6" />
          </button>
        </div>
      )}

      {/* Mobile note bottom sheet */}
      {showMobileNoteSheet && (
        <div className="fixed inset-0 z-50 md:hidden flex flex-col justify-end">
          <div className="absolute inset-0 bg-black/40" onClick={noteDiscardGuard.requestClose} />
          <div className="relative bg-app-surface rounded-t-2xl shadow-xl flex flex-col max-h-[80vh]">
            <div className="flex items-center justify-between px-4 py-3 border-b border-app-border">
              <h3 className="text-base font-semibold text-app-text">Notiz</h3>
              <button type="button" onClick={noteDiscardGuard.requestClose} className="p-1.5 rounded-lg hover:bg-app-hover text-app-text-subtle">
                <X className="w-5 h-5" />
              </button>
            </div>
            <textarea
              className="flex-1 min-h-[200px] p-4 text-sm text-app-text resize-none focus:outline-none"
              placeholder="Hier Notiz eingeben …"
              value={mobileNoteContent}
              onChange={(e) => setMobileNoteContent(e.target.value)}
              autoFocus
            />
            <div className="flex gap-2 px-4 py-3 border-t border-app-border">
              <button
                type="button"
                onClick={() => {
                  setMobileNoteContent('');
                  setShowMobileNoteSheet(false);
                }}
                className="flex-1 py-2.5 rounded-xl bg-app-surface-muted text-app-text-muted text-sm font-semibold"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={async () => {
                  await saveNotes(mobileNoteContent);
                  setMobileNoteContent('');
                  setShowMobileNoteSheet(false);
                }}
                disabled={isSaving || !mobileNoteContent.trim()}
                className="flex-1 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-semibold disabled:opacity-50"
              >
                {isSaving ? 'Speichert…' : 'Speichern'}
              </button>
            </div>
          </div>
        </div>
      )}
      {noteDiscardGuard.dialog}
    </>
  );
}
